import { LoadingButton } from '@mui/lab'
import i18n from '@services/i18n/i18n'
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog'
import { CreateEditLayout } from '@shared/layouts/CreateEditLayout'
import { CreateTopicInput, useCreateTopicMutation } from '@typings/graphql'
import React from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { DefaultSnackbar } from '@shared/components/ui/DefaultSnackbar'
import { useErrorMessage } from '@hooks/useErrorMessage'
import { EditableFile } from '@typings/files'
import { useUnsavedChanges } from '@hooks/useUnsavedChanges'

import { TopicCreateEditInputs } from '../components/TopicCreateEditInputs'
import { TopicsProvider } from '../provider/TopicsProvider'

type TopicInputType = Omit<CreateTopicInput, 'texts'> & {
  texts: Record<string, string>
  files: Record<string, EditableFile<any> | null>
}

const TopicsCreateViewContent: React.FC = () => {
  const { t } = useTranslation()
  const { professionId } = useParams<{ professionId: string }>()

  const [loading, setLoading] = React.useState(false)
  const [showCreatedDialog, setShowCreatedDialog] = React.useState(false)
  const [showToast, setShowToast] = React.useState(false)
  const [created, setCreated] = React.useState(false)

  const { setMessageByCode, errorMessage } = useErrorMessage()

  const navigate = useNavigate()

  const { formState: { dirtyFields, isDirty, isValid, isSubmitted }, handleSubmit } = useFormContext()

  useUnsavedChanges(isDirty && !created && (!isSubmitted || !!errorMessage))

  const [createTopic] = useCreateTopicMutation()

  const onSubmit = handleSubmit(async (input) => {
    setLoading(true)

    try {
      await createTopic({
        variables: {
          data: {
            professionId: professionId as string,
            index: input.index,
            trainingYear: input.trainingYear,
            texts: Object.keys(input.texts).map((key) => ({
              key,
              lang: i18n.resolvedLanguage,
              value: input.texts[key]
            }))
          }
        }
      })

      setCreated(true)
      setShowCreatedDialog(true)
    } catch (e) {
      setMessageByCode(e)
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  })

  const handleConfirm = () => {
    navigate(`/professions/${professionId}/topics`)
  }

  return (
    <CreateEditLayout
      title={t('topics.createTitle')}
      description={t('topics.createDescription')}
      buttonComp={(
        <LoadingButton
          variant="contained"
          type='submit'
          loading={loading}
          onClick={onSubmit}
          disabled={!dirtyFields || !isValid}
        >
          {t('common.save')}
        </LoadingButton>
      )}
    >
      <TopicCreateEditInputs />

      <ConfirmDialog
        title={t('common.entryCreated')}
        open={showCreatedDialog}
        hideCancel
        onConfirm={handleConfirm}
      >
        {t('topics.createCompleted')}
      </ConfirmDialog>

      <DefaultSnackbar
        open={showToast}
        severity={'error'}
        onClose={() => setShowToast(false)}
        message={errorMessage}
      />
    </CreateEditLayout>
  )
}

export const TopicsCreateView: React.FC = () => {
  const formData = useForm<TopicInputType>({
    defaultValues: {},
    mode: 'onChange'
  })

  return (
    <TopicsProvider>
      <FormProvider {...formData}>
        <TopicsCreateViewContent />
      </FormProvider>
    </TopicsProvider>
  )
}
